/**
 * Theme Configuration
 * Easily configurable color theme for the Trade Declaration Platform
 * Default: Celtic Blue #236EC3 (R35 G110 B195)
 */

export const themeConfig = {
  // Semantic colors
  colors: {
    success: "#5A913D",
    warning: "#F0BC00",
    error: "#C54A31",
    info: "#077FA1",
  },

  // Light theme
  light: {
    primary: {
      main: "#FF0000",
      light: "#FF3333",
      dark: "#CC0000",
      rgb: "255, 0, 0",
    },
    background: "#FFFFFF",
    surface: "#F9F9F9",
    surfaceElevated: "#FFFFFF",
    textPrimary: "#202020",
    textSecondary: "#666666",
    textMuted: "#999999",
    border: "#E6E6E6",
    borderLight: "#F2F2F2",
    shadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    shadowLarge:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },

  // Dark theme
  dark: {
    primary: {
      main: "#FF4D4D",
      light: "#FF8080",
      dark: "#E60000",
      rgb: "255, 0, 0",
    },
    background: "#000000",
    surface: "#2D2D2D",
    surfaceElevated: "#333333",
    textPrimary: "#F9F9F9",
    textSecondary: "#CCCCCC",
    textMuted: "#999999",
    border: "#333333",
    borderLight: "#202020",
    shadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)",
    shadowLarge:
      "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)",
  },

  // Typography
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizes: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
    },
  },

  // Spacing
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    "2xl": "3rem",
    "3xl": "4rem",
    "4xl": "6rem",
  },

  // Breakpoints
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },

  // Border radius
  borderRadius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    full: "9999px",
  },

  // Transitions
  transitions: {
    fast: "150ms ease-in-out",
    base: "200ms ease-in-out",
    slow: "300ms ease-in-out",
  },
};

// Function to apply CSS variables based on theme
export const applyTheme = (isDark, element = document.documentElement) => {
  const theme = isDark ? themeConfig.dark : themeConfig.light;
    
  // Apply primary colors
  element.style.setProperty('--color-primary', theme.primary.main);
  element.style.setProperty('--color-primary-light', theme.primary.light);
  element.style.setProperty('--color-primary-dark', theme.primary.dark);
  element.style.setProperty('--color-primary-rgb', theme.primary.rgb);
  
  // Apply semantic colors
  element.style.setProperty('--color-success', themeConfig.colors.success);
  element.style.setProperty('--color-warning', themeConfig.colors.warning);
  element.style.setProperty('--color-error', themeConfig.colors.error);
  element.style.setProperty('--color-info', themeConfig.colors.info);
  
  // Apply theme colors
  element.style.setProperty('--color-background', theme.background);
  element.style.setProperty('--color-surface', theme.surface);
  element.style.setProperty('--color-surface-elevated', theme.surfaceElevated);
  element.style.setProperty('--color-text-primary', theme.textPrimary);
  element.style.setProperty('--color-text-secondary', theme.textSecondary);
  element.style.setProperty('--color-text-muted', theme.textMuted);
  element.style.setProperty('--color-border', theme.border);
  element.style.setProperty('--color-border-light', theme.borderLight);
  
  // Apply shadows
  element.style.setProperty('--shadow-sm', theme.shadow);
  element.style.setProperty('--shadow-lg', theme.shadowLarge);
  
  // Apply data attribute for CSS selectors
  element.setAttribute("data-theme", isDark ? "dark" : "light");
};
